// window.ui 네임스페이스 초기화
window.ui = window.ui || {};

/**
 * 토스트 알림 관리 유틸리티
 */
const AlertManager = {
  /**
   * 알림 메시지 표시
   * @param {string} message - 표시할 메시지
   * @param {string} type - 알림 타입 ('info' | 'success' | 'warning' | 'error')
   * @param {number} duration - 표시 시간 (ms, 기본값: 3000)
   */
  show(message, type = 'info', duration = 3000) {
    // 기존 알림 제거
    const existingAlert = document.querySelector('.toast-message');
    if (existingAlert) {
      existingAlert.remove();
    }

    // 새 알림 생성
    const alertDiv = document.createElement('div');
    alertDiv.className = `toast-message toast-message--${type}`;
    alertDiv.innerHTML = `
      <div class="toast-message__content">
        <span class="toast-message__message">${message}</span>
        <button class="toast-message__close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;

    document.body.appendChild(alertDiv);

    // duration 후 자동 제거
    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.classList.add('removing');
        setTimeout(() => {
          if (alertDiv.parentNode) {
            alertDiv.remove();
          }
        }, 300);
      }
    }, duration);
  },

  /**
   * 성공 알림
   */
  success(message, duration) {
    this.show(message, 'success', duration);
  },

  /**
   * 경고 알림
   */
  warning(message, duration) {
    this.show(message, 'warning', duration);
  },

  /**
   * 에러 알림
   */
  error(message, duration) {
    this.show(message, 'error', duration);
  },

  /**
   * 정보 알림
   */
  info(message, duration) {
    this.show(message, 'info', duration);
  }
};

/**
 * 레이어(Modal/Dialog) 관리 유틸리티
 */
const LayerManager = {
  // 열린 dialog 관리용 Map (private)
  _openDialogs: new Map(),

  /**
   * 모달 열기 (수동 이벤트 관리)
   * @param {HTMLDialogElement} dialog - 대상 dialog 엘리먼트
   * @param {Object} options - 옵션
   * @returns {Function} cleanup - 이벤트 제거 함수
   */
  show(dialog, options = {}) {
    if (!dialog || !(dialog instanceof HTMLDialogElement)) {
      console.error('유효한 dialog 엘리먼트가 필요합니다.');
      return () => {};
    }

    const {
      method = 'showModal', // 'show' 또는 'showModal'
      onClose = null,
      onShow = null,
      outsideClickClose = true,
      disableScroll = true,
      opener = null
    } = options;

    console.log(window.event);

    // dialog 열기
    dialog[method]();

    const dialogInfo = {
      onClose,
      onShow,
      opener,
      disableScroll,
      outsideClickClose,
      openedAt: Date.now()
    };

    // dialog 정보를 Map에 저장
    this._openDialogs.set(dialog, dialogInfo);

    if (disableScroll) {
      this._lockScroll(dialog);
    }

    if (onShow) {
      onShow?.(dialogInfo, this);
    }

    // 닫기 이벤트 핸들러
    const handleClose = (e) => {
      this._closeWithAnimation(dialog, () => {
        const dialogInfo = this._openDialogs.get(dialog);

        if (dialogInfo) {
          if (dialogInfo.disableScroll) {
            this._unlockScroll(dialog);
          }
          // 저장된 onClose 실행
          if (dialogInfo.onClose) {
            dialogInfo.onClose(dialogInfo, this, e);
          }
        }
      });
    };

    // ESC 키로 닫을 때
    const handleCancel = (e) => {
      e.preventDefault();
      handleClose(e);
    };

    // backdrop 클릭으로 닫을 때
    const handleBackdropClick = (e) => {
      if (e.target === dialog) {
        handleClose(e);
      }
    };

    // cleanup 함수
    const cleanup = () => {
      dialog.removeEventListener('cancel', handleCancel);
      if (outsideClickClose) {
        dialog.removeEventListener('click', handleBackdropClick);
      }

      const dialogInfo = this._openDialogs.get(dialog);
      if (dialogInfo && dialogInfo.disableScroll) {
        this._unlockScroll(dialog);
      }

      // Map에서 제거
      this._openDialogs.delete(dialog);
    };

    // 애니메이션을 위한 is-open 클래스 추가
    requestAnimationFrame(() => {
      dialog.classList.add('is-open');
    });

    // 이벤트 등록
    dialog.addEventListener('cancel', handleCancel);
    if (outsideClickClose) {
      dialog.addEventListener('click', handleBackdropClick);
    }

    // close 이벤트 발생 시 자동으로 cleanup 실행
    dialog.addEventListener('close', cleanup, { once: true });

    return cleanup;
  },

  /**
   * 애니메이션과 함께 닫기
   * @private
   */
  _closeWithAnimation(dialog, callback) {
    // is-open 클래스 제거
    dialog.classList.remove('is-open');

    // transition이 적용된 요소 찾기 (.spvs-modal-container 우선)
    const dialogBody = dialog.querySelector('.spvs-modal-container');
    const computedStyle = dialogBody ? window.getComputedStyle(dialogBody) : window.getComputedStyle(dialog);
    const hasTransition = computedStyle.transitionDuration !== '0s';

    if (hasTransition) {
      // transition 종료 후 닫기
      const transitionTarget = dialogBody || dialog;
      const handleTransitionEnd = (e) => {
        if (e.target === transitionTarget) {
          transitionTarget.removeEventListener('transitionend', handleTransitionEnd);
          dialog.close();
          if (callback) callback();
        }
      };

      transitionTarget.addEventListener('transitionend', handleTransitionEnd);

      // 안전장치: transition이 실행되지 않을 경우를 대비
      setTimeout(() => {
        if (dialog.open) {
          transitionTarget.removeEventListener('transitionend', handleTransitionEnd);
          dialog.close();
          if (callback) callback();
        }
      }, 500);
    } else {
      // transition이 없으면 바로 닫기
      dialog.close();
      if (callback) callback();
    }
  },

  /**
   * 스크롤 잠금
   * @private
   */
  _lockScroll(dialog) {
    if (!window.bodyScrollLock) {
      console.warn('body-scroll-lock 라이브러리가 로드되지 않았습니다.');
      return;
    }

    window.bodyScrollLock.disableBodyScroll(dialog, {
      allowTouchMove: (el) => {
        // body-scroll-lock-ignore 속성이 있는 요소는 스크롤 허용
        while (el && el !== document.body) {
          if (el.getAttribute('body-scroll-lock-ignore') !== null) {
            return true;
          }
          el = el.parentElement;
        }
        return false;
      }
    });
  },

  /**
   * 스크롤 잠금 해제
   * @private
   */
  _unlockScroll(dialog) {
    if (window.bodyScrollLock) {
      window.bodyScrollLock.enableBodyScroll(dialog);
    }
  },

  /**
   * 프로그래밍 방식으로 모달 닫기
   * @param {HTMLDialogElement} dialog - 대상 dialog 엘리먼트
   * @param {Function} callback - 닫힌 후 실행할 추가 콜백 (선택사항)
   */
  close(dialog, callback) {
    if (!dialog || !dialog.open) return;

    // 애니메이션과 함께 닫기
    // _closeWithAnimation 내부에서 dialog.close()가 호출되면
    // 'close' 이벤트가 발생하여 show()에서 등록한 cleanup이 자동 실행됨
    // cleanup에서 저장된 onClose도 함께 실행됨
    this._closeWithAnimation(dialog, () => {
      if (callback) callback();
    });
  },

  /**
   * 현재 열린 모든 dialog 가져오기
   * @returns {Array} 열린 dialog 정보 배열
   */
  getOpenDialogs() {
    return Array.from(this._openDialogs.entries()).map(([dialog, info]) => ({
      dialog,
      ...info
    }));
  },

  /**
   * 특정 dialog 정보 가져오기
   * @param {HTMLDialogElement} dialog
   * @returns {Object|null} dialog 정보
   */
  getDialogInfo(dialog) {
    return this._openDialogs.get(dialog) || null;
  },

  /**
   * opener로 dialog 찾기
   * @param {HTMLElement} opener
   * @returns {HTMLDialogElement|null}
   */
  findDialogByOpener(opener) {
    for (const [dialog, info] of this._openDialogs.entries()) {
      if (info.opener === opener) {
        return dialog;
      }
    }
    return null;
  },

  /**
   * 모든 열린 dialog 닫기
   */
  closeAll() {
    const dialogs = Array.from(this._openDialogs.keys());
    dialogs.forEach((dialog) => this.close(dialog));
  }
};

/**
 * 반응형 Resize 이벤트 관리 유틸리티
 */
const ResizeManager = {
  // 현재 브레이크포인트
  _currentBreakpoint: null,

  // 브레이크포인트 정의
  breakpoints: {
    mobile: 0,
    tablet: 768,
    pc: 1024
  },

  // 등록된 콜백 함수들
  _callbacks: [],

  // 디바운스 타이머
  _resizeTimer: null,
  _debounceDelay: 150,

  /**
   * 초기화
   */
  init() {
    this._currentBreakpoint = this._getBreakpoint();
    this._bindEvents();

    // 초기 실행
    this._handleResize();
  },

  /**
   * 이벤트 바인딩
   * @private
   */
  _bindEvents() {
    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this._handleResize();
      }, this._debounceDelay);
    });
  },

  /**
   * resize 이벤트 핸들러
   * @private
   */
  _handleResize() {
    const newBreakpoint = this._getBreakpoint();
    const oldBreakpoint = this._currentBreakpoint;
    const hasBreakpointChanged = newBreakpoint !== oldBreakpoint;

    // 브레이크포인트가 변경되었을 때만 콜백 실행
    if (hasBreakpointChanged) {
      this._currentBreakpoint = newBreakpoint;

      this._callbacks.forEach((callback) => {
        callback({
          currentBreakpoint: newBreakpoint,
          previousBreakpoint: oldBreakpoint,
          width: window.innerWidth,
          height: window.innerHeight
        });
      });
    }
  },

  /**
   * 현재 브레이크포인트 가져오기
   * @private
   */
  _getBreakpoint() {
    const width = window.innerWidth;

    if (width >= this.breakpoints.pc) {
      return 'pc';
    } else if (width >= this.breakpoints.tablet) {
      return 'tablet';
    } else {
      return 'mobile';
    }
  },

  /**
   * 브레이크포인트 변경 시 실행할 콜백 등록
   * @param {Function} callback - 실행할 콜백 함수
   * @returns {Function} 콜백 제거 함수
   */
  onBreakpointChange(callback) {
    if (typeof callback !== 'function') {
      console.error('콜백은 함수여야 합니다.');
      return () => {};
    }

    this._callbacks.push(callback);

    // 콜백 제거 함수 반환
    return () => {
      const index = this._callbacks.indexOf(callback);
      if (index > -1) {
        this._callbacks.splice(index, 1);
      }
    };
  },

  /**
   * 현재 브레이크포인트 반환
   * @returns {string} 'mobile' | 'tablet' | 'pc'
   */
  getCurrentBreakpoint() {
    return this._currentBreakpoint;
  },

  /**
   * 특정 브레이크포인트인지 확인
   * @param {string} breakpoint
   * @returns {boolean}
   */
  is(breakpoint) {
    return this._currentBreakpoint === breakpoint;
  },

  /**
   * 모바일인지 확인
   * @returns {boolean}
   */
  isMobile() {
    return this._currentBreakpoint === 'mobile';
  },

  /**
   * 태블릿인지 확인
   * @returns {boolean}
   */
  isTablet() {
    return this._currentBreakpoint === 'tablet';
  },

  /**
   * PC인지 확인
   * @returns {boolean}
   */
  isPc() {
    return this._currentBreakpoint === 'pc';
  },

  /**
   * 브레이크포인트 커스터마이징
   * @param {Object} breakpoints
   */
  setBreakpoints(breakpoints) {
    this.breakpoints = { ...this.breakpoints, ...breakpoints };
    this._currentBreakpoint = this._getBreakpoint();
  }
};

/**
 * 스크롤 관리 유틸리티
 */
const ScrollManager = {
  // 백투탑 버튼 설정
  _backToTopButton: null,
  _footer: null,

  // 스크롤 상태 관리
  _isScrolling: false,
  _scrollThreshold: 100,
  _scrollUpThreshold: 50, // 위로 스크롤할 때의 임계값 (히스테리시스)
  _isScrolledState: false, // 현재 스크롤 상태 저장

  // 등록된 콜백 함수들
  _callbacks: [],

  /**
   * 초기화
   */
  init() {
    this._backToTopButton = document.querySelector('#backToTop');
    this._footer = document.querySelector('#footer');

    if (!this._backToTopButton) {
      return;
    }

    this._bindEvents();
    this._handleScroll(); // 초기 상태 설정
  },

  /**
   * 이벤트 바인딩
   * @private
   */
  _bindEvents() {
    // 스크롤 이벤트
    window.addEventListener('scroll', () => this._handleScroll(), { passive: true });

    // 리사이즈 이벤트
    window.addEventListener('resize', () => this._updateFooterVisibleHeight(), { passive: true });

    // 버튼 클릭 이벤트
    this._backToTopButton.addEventListener('click', () => this._scrollToTop());
  },

  /**
   * 스크롤 이벤트 핸들러
   * @private
   */
  _handleScroll() {
    if (this._isScrolling) return;

    this._isScrolling = true;

    requestAnimationFrame(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // 히스테리시스를 적용한 스크롤 상태 판단
      let isScrolled = this._isScrolledState;

      if (!this._isScrolledState && scrollTop > this._scrollThreshold) {
        // 스크롤되지 않은 상태에서 임계값을 넘으면 스크롤됨
        isScrolled = true;
      } else if (this._isScrolledState && scrollTop < this._scrollUpThreshold) {
        // 스크롤된 상태에서 더 낮은 임계값 아래로 가면 스크롤되지 않음
        isScrolled = false;
      }

      const hasStateChanged = isScrolled !== this._isScrolledState;
      this._isScrolledState = isScrolled;

      // 백투탑 버튼도 같은 스크롤 상태 사용 (헤더와 동기화)
      if (hasStateChanged) {
        this._toggleBackToTopButton(isScrolled);
        this._toggleScrolledClass(isScrolled);
      }

      // footer 높이 업데이트
      this._updateFooterVisibleHeight();

      // 등록된 콜백들 실행 (상태 변경 여부와 함께 전달)
      this._executeCallbacks({
        scrollTop,
        isVisible: isScrolled, // 백투탑 버튼 표시 여부도 동일한 상태 사용
        isScrolled: isScrolled,
        hasStateChanged: hasStateChanged,
        threshold: this._scrollThreshold
      });

      this._isScrolling = false;
    });
  },

  /**
   * 백투탑 버튼 노출/비노출 토글
   * @private
   * @param {boolean} isScrolled
   */
  _toggleBackToTopButton(isScrolled) {
    if (isScrolled) {
      this._backToTopButton.classList.add('active');
    } else {
      this._backToTopButton.classList.remove('active');
    }
  },

  /**
   * 스크롤 상태에 따른 body 클래스 토글
   * @private
   * @param {boolean} isScrolled
   */
  _toggleScrolledClass(isScrolled) {
    if (isScrolled) {
      document.body.classList.add('is-scrolled');
    } else {
      document.body.classList.remove('is-scrolled');
    }
  },

  /**
   * footer와 겹치지 않도록 CSS 변수 업데이트
   * @private
   */
  _updateFooterVisibleHeight() {
    if (!this._footer) {
      this._backToTopButton.style.setProperty('--footer-visible-height', '0px');
      return;
    }

    const footerRect = this._footer.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // footer가 화면 하단에서 얼마나 보이는지 계산
    let footerVisibleHeight = 0;

    if (footerRect.top < windowHeight) {
      footerVisibleHeight = Math.max(0, windowHeight - footerRect.top);
    }

    this._backToTopButton.style.setProperty('--footer-visible-height', `${footerVisibleHeight}px`);
  },

  /**
   * 부드러운 스크롤로 맨 위로 이동
   * @private
   */
  _scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  },

  /**
   * 등록된 콜백들 실행
   * @private
   * @param {Object} data
   */
  _executeCallbacks(data) {
    this._callbacks.forEach((callback) => {
      callback(data);
    });
  },

  /**
   * 스크롤 이벤트 콜백 등록
   * @param {Function} callback - 실행할 콜백 함수
   * @returns {Function} 콜백 제거 함수
   */
  onScroll(callback) {
    if (typeof callback !== 'function') {
      console.error('콜백은 함수여야 합니다.');
      return () => {};
    }

    this._callbacks.push(callback);

    // 콜백 제거 함수 반환
    return () => {
      const index = this._callbacks.indexOf(callback);
      if (index > -1) {
        this._callbacks.splice(index, 1);
      }
    };
  },

  /**
   * 스크롤 임계값 설정
   * @param {number} threshold - 아래로 스크롤할 때의 임계값
   * @param {number} upThreshold - 위로 스크롤할 때의 임계값 (선택사항)
   */
  setScrollThreshold(threshold, upThreshold = null) {
    this._scrollThreshold = threshold;
    this._scrollUpThreshold = upThreshold || threshold - 50; // 기본값: 50px 차이
  },

  /**
   * 현재 스크롤 위치 반환
   * @returns {number}
   */
  getCurrentScrollTop() {
    return window.pageYOffset || document.documentElement.scrollTop;
  },

  /**
   * 현재 스크롤 상태 확인 (임계값 이상인지)
   * @returns {boolean}
   */
  isScrolled() {
    return this._isScrolledState;
  },

  /**
   * 백투탑 버튼이 활성화 상태인지 확인
   * @returns {boolean}
   */
  isBackToTopVisible() {
    return this._isScrolledState;
  },

  /**
   * 프로그래밍 방식으로 맨 위로 스크롤
   * @param {Object} options
   */
  scrollToTop(options = {}) {
    const { behavior = 'smooth', top = 0 } = options;

    window.scrollTo({
      top,
      behavior
    });
  }
};

// window.ui 네임스페이스에 등록
window.ui.alert = AlertManager;
window.ui.layer = LayerManager;
window.ui.resize = ResizeManager;
window.ui.scroll = ScrollManager;

// DOMContentLoaded 이벤트 핸들러
document.addEventListener('DOMContentLoaded', () => {
  window.ui.scroll.init();
  window.ui.resize.init();

  window.ui.resize.onBreakpointChange((data) => {
    const { currentBreakpoint } = data;
    const openDialogs = window.ui.layer.getOpenDialogs();

    openDialogs.forEach(({ dialog }) => {
      const deviceType = dialog.dataset.device;

      // 모바일 전용: PC로 전환 시 닫기
      if (deviceType === 'mobile-only' && currentBreakpoint === 'pc') {
        window.ui.layer.close(dialog);
      }

      // PC 전용: 모바일/태블릿으로 전환 시 닫기
      if (deviceType === 'pc-only' && (currentBreakpoint === 'mobile' || currentBreakpoint === 'tablet')) {
        window.ui.layer.close(dialog);
      }
    });
  });

  // 모바일 메뉴, 장바구니 레이어
  const dialog = document.querySelector('#mobileHeaderNav');
  const bottomSheet = document.querySelector('#mobileCartSheet');
  const toggleMenu = document.querySelector('#openMobileMenu');
  const bottomSheetToggle = document.querySelector('#openMobileCart');

  if (toggleMenu && dialog) {
    toggleMenu.addEventListener('click', () => {
      window.ui.layer.show(dialog, {
        outsideClickClose: false
      });
    });
  }

  if (bottomSheetToggle && bottomSheet) {
    bottomSheetToggle.addEventListener('click', () => {
      window.ui.layer.show(bottomSheet, {
        onShow: () => {
          bottomSheetToggle.classList.add('spvs-button--active');
        },
        onClose: () => {
          bottomSheetToggle.classList.remove('spvs-button--active');
        },
        disableScroll: true
      });
    });
  }
});
